import { HttpHeaders } from '@angular/common/http';
import { FieldMergeFunction, FieldPolicy, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { Reference, relayStylePagination } from '@apollo/client/utilities';
import { TRelayPageInfo } from '@apollo/client/utilities/policies/pagination';
import { HttpLink } from 'apollo-angular/http';
import { orderBy as _orderBy } from 'lodash-es';
import produce from 'immer';
import { __rest } from 'tslib';

import { environment } from 'environments/environment';
import generatedIntrospection from 'generated/introspection-result';

import { cursorSubtract } from 'helpers';

import { ApolloErrorHandlerService, IGraphQLError } from './apollo-error-handler.service';

export const campaignContextIdKeyFn =
  (result, _ctx) => `${result.__typename}:c${result.campaignContextId || null}:${result.id}`;


type KeyArgs = FieldPolicy<any>['keyArgs'];

export type TRelayNodes<TNode = Reference> = Readonly<{
  nodes: TNode[];
  pageInfo: TRelayPageInfo;
}>;

export const concatNodesFieldMergeFn: FieldMergeFunction<TRelayNodes, TRelayNodes> =
  (existing, incoming, options) => {
    const { args, readField, toReference } = options;
    const getExtras = (obj: Record<string, any>) => __rest(obj, notExtras);
    const notExtras = ['nodes', 'pageInfo'];
    if (!existing) {
      return incoming;
    }

    const existingNodeRefs = existing.nodes.map(node => toReference(node));
    const newNodes = incoming.nodes
      .filter(nn => !existingNodeRefs.map(r => r.__ref).includes(toReference(nn).__ref));

    return {
      ...getExtras(existing),
      ...getExtras(incoming),
      nodes: [
        ...existingNodeRefs,
        ...newNodes
      ],
      pageInfo: incoming.pageInfo
    };
  };


/**
 * Configures apollo client
 */
export function setupApollo(
  httpLink: HttpLink,
  apolloErrorHandlerService: ApolloErrorHandlerService,
) {
  /**
   * Creates an angular link
   * which also adds the auth headers
   * to each request.
   */
  const angularHttpLink = httpLink.create(
    {
      uri: `${environment.apiBase}/api/graphql`,
      headers: new HttpHeaders({
        'access-token': localStorage.getItem('accessToken') || '',
        client: localStorage.getItem('client') || '',
        expiry: localStorage.getItem('expiry') || '',
        'token-type': localStorage.getItem('tokenType') || '',
        uid: localStorage.getItem('uid') || ''
      })
    });

  /** Error handling link */
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (operation.getContext()?.ignoreErrors) {
      return;
    }
    if (graphQLErrors) {
      graphQLErrors.forEach((gqlError) => {
        const err = gqlError as any as IGraphQLError;
        apolloErrorHandlerService.handleError(err);
      });
    }
    if (networkError) {
      console.warn(`[Network error]:`, networkError);
    }
  });

  // Set up apollo cache
  const cache = new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
    typePolicies: {
      Action: {
        fields: {
          actionAssignmentGroups: {
            merge(_existing = [], incoming, _options) {
              return incoming;
            }
          }
        }
      },
      EntityConnection: {
        fields: {
          tags: {
            merge: false
          }
        }
      },
      CalendarEventResponse: {
        fields: {
          appliedTags: {
            merge(_existing = [], incoming, _options) {
              return incoming;
            }
          }
        }
      },
      CampaignEntityConnectionTypeConfiguration: {
        fields: {
          toplineSettingItems: {
            merge(_existing = [], incoming, _options) {
              return incoming;
            }
          }
        }
      },
      CampaignEntityTypeConfiguration: {
        fields: {
          defaultTagCategories: {
            merge(_existing = [], incoming, _options) {
              return incoming;
            }
          },
          spotlightTags: {
            merge(_existing = [], incoming, _options) {
              return incoming;
            }
          }
        }
      },
      CustomForm: {
        fields: {
          fields: {
            merge(_existing = [], incoming, _options) {
              return incoming;
            }
          }
        }
      },
      Entity: {
        keyFields: campaignContextIdKeyFn,
        // merge: false,
        fields: {
          tags: {
            merge: false
          },
          notes: {
            merge(_existing = [], incoming, _options) {
              return incoming;
            }
          },
          currentAssessmentLevel: {
            merge(existing, incoming, { mergeObjects }) {
              return incoming;
            }
          }
        }
      },
      ActionNetworkIntegration: {
        fields: {
          mappings: {
            merge: false
          }
        }
      },
      AppliedToplines: {
        keyFields: campaignContextIdKeyFn
      },
      Tag: {
        keyFields: (storeObject, ctx) => `Tag:${storeObject.id}`
      },
      TagGroup: {
        keyFields: campaignContextIdKeyFn,
        fields: {
          categories: {
            read(existing = [], { readField }) {
              return _orderBy(
                existing,
                (cat) => readField<string>('name', cat)?.toLowerCase()
              );
            }
          }
        }
      },
      TagCategory: {
        keyFields: campaignContextIdKeyFn,
      },
      Organization: {
        keyFields: ['name']
      },
      // Formerly Cache Redirects
      Query: {
        fields: {
          getTagById(existingData, { args, toReference }) {
            return existingData || toReference({ __typename: 'Tag', id: args.tagId });
          },
          getEntity(existingData, { args, toReference }) {
            return existingData || toReference({ __typename: 'Entity', id: args.entityId, campaignContextId: args.campaignId });
          },
          // https://github.com/apollographql/apollo-client/blob/main/src/utilities/policies/pagination.ts
          // https://www.apollographql.com/docs/react/pagination/cursor-based/
          searchTags: relayStylePagination(
            [
              'keyword',
              'tagCategoryIds',
              'status',
              'orderBy',
              'isInCampaignId',
              'campaignId',
              'targetType',
              'targetTypeId',
              'campaignId',
              'tagTypes'
            ]
          ),
          searchCampaigns: {
            keyArgs: ['keyword', 'first', 'after', 'withEntitiesCount', 'status']
          },
          searchUsers: {
            keyArgs: ['keyword', 'first', 'after', 'withEntitiesCount', 'activeOnly', 'pendingOnly', 'disabledOnly', 'campaignId']
          },
          getSavedQueries: {
            keyArgs: ['campaignId', 'keyword', 'queryType', 'after']
          },
          searchCampaignObjectVisibility: {
            keyArgs: [
              'objectTypeId',
              'objectType',
              'visibility',
              'keyword',
              'first',
              'after'
            ]
          },
          getUploads: {
            keyArgs: [
              'first',
              'after',
              'visiblityStatus'
            ]
          },
          getEntityConnections: {
            keyArgs: [
              'entityId',
              'campaignId',
              'entityConnectionTypeId',
              'includeInactive'
            ],
            merge(existing: any, incoming, options) {
              const result = concatNodesFieldMergeFn(existing, incoming, options);
              if (!options.args.includeInactive) {
                return {
                  ...result,
                  ...{ nodes: result.nodes.filter(n => options.readField<boolean>('active', n)) }
                };
              }
              return result;
            },
            read(existing, { args, readField, toReference }) {
              if (!existing) {
                return;
              };

              const nextState = produce(existing, draftState => {
                const activeOnly = args.includeInactive !== true;
                // Used to remove inactive connections after set as inactive
                // with forcing a refetch of the entire list
                const inactiveInExisting = existing.nodes.filter(n => !readField<boolean>('active', n));

                const inactiveInExistingCount = inactiveInExisting.length;

                // Adjust total count to adjust for previously fetched inactive nodes.
                draftState.totalCount = activeOnly ? existing.totalCount - inactiveInExistingCount : existing.totalCount;

                // Adjust cursor
                draftState.pageInfo.endCursor = activeOnly ?
                  cursorSubtract(existing.pageInfo.endCursor, inactiveInExistingCount)
                  : existing.pageInfo.endCursor;

                draftState.nodes = activeOnly ? existing.nodes
                  .filter(n => readField<boolean>('active', n)) : existing.nodes;

              });

              return nextState;
            }
          }
        }
      }
    }
  });

  // Creates apollo client
  return {
    link: errorLink.concat(angularHttpLink),
    cache
  };
}
